import React, { useState, useEffect, useContext } from "react";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import {
  FaSearch,
  FaFileExcel,
  FaPrint,
  FaFilter,
  FaTimes,
  FaBox,
  FaExclamationTriangle,
  FaWarehouse,
  FaBoxes,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";

const ReorderSparesReport = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryButtonClass = `bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white`;
  const secondaryButtonClass =
    "bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white";

  // localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const storedBranchId = localStorage.getItem("branchId") || "8";
  const storedBranchType = localStorage.getItem("branchType") || "HO";
  const userId = localStorage.getItem("userId") || "";

  // common headers
  const commonHeaders = {
    "x-finyearid": finYearId,
    "x-finyear": finYear,
    "x-branchid": storedBranchId,
    "x-branch": storedBranchType,
    "x-userid": userId,
  };

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  const [branchId, setBranchId] = useState(storedBranchId || "8");
  const [itemCode, setItemCode] = useState("");
  const [itemName, setItemName] = useState("");
  const [stockFilter, setStockFilter] = useState("");
  const [store, setStore] = useState("");
  const [category, setCategory] = useState("");
  const [categoryList, setCategoryList] = useState([]);
  const [storeList, setStoreList] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [data, setData] = useState([]);

  useEffect(() => {
    setLoading(true);

    const reorderDropdownReq = axios.get(
      "http://nissi-india.com/api/reOrder_sparesRpt",
      {
        params: {
          branchId: branchId || storedBranchId || "8",
        },
        headers: commonHeaders,
      }
    );

    const branchReq = axios.get("http://nissi-india.com/api/CatWise_Rpt", {
      params: {
        branchType: storedBranchType || "HO",
        branchId: branchId || storedBranchId || "8",
      },
      headers: commonHeaders,
    });

    Promise.all([reorderDropdownReq, branchReq])
      .then(([reorderRes, branchRes]) => {
        const stores =
          reorderRes.data.data?.stores ||
          reorderRes.data.stores ||
          [];
        const categories =
          reorderRes.data.data?.categories ||
          reorderRes.data.categories ||
          [];
        const branches = branchRes.data.branches || [];

        setCategoryList(categories);
        setStoreList(stores);
        setBranchList(branches);

        if (!branchId && branches.length > 0) {
          setBranchId(branches[0].value?.toString() || storedBranchId || "8");
        }
      })
      .catch((err) => {
        console.error("Dropdown fetch failed", err);
        toast.error("Failed to load dropdowns");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const getStockStatus = (minStock, maxStock, closingStock) => {
    if (closingStock <= minStock) return "critical";
    if (closingStock <= maxStock * 0.3) return "low";
    return "adequate";
  };

  const handleSearch = () => {
    setLoading(true);

    const payload = {
      branchId: branchId || "%",
      storeId: store || "%",
      categoryId: category || "%",
      itemName: itemName || "%",
      itemCode: itemCode || "%",
      maxstock: stockFilter || "%",
    };

    axios
      .get("http://nissi-india.com/api/reorderSpares_listRpt", {
        params: payload,
        headers: commonHeaders,
      })
      .then((res) => {
        const rows =
          res.data.data ||
          res.data.rows ||
          res.data.recordset ||
          res.data.recordsets?.[0] ||
          [];

        setData(rows);
        toast.success(`Loaded ${rows.length} records`);
      })
      .catch((err) => {
        console.error("Reorder spares fetch failed", err);
        toast.error("Failed to fetch reorder spares report");
        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleExcelDownload = () => {
    const payload = {
      branchId: branchId || "%",
      storeId: store || "%",
      categoryId: category || "%",
      itemName: itemName || "%",
      itemCode: itemCode || "%",
      maxstock: stockFilter || "%",
    };

    axios
      .get("http://nissi-india.com/api/reorderSpares_excel", {
        params: payload,
        headers: commonHeaders,
        responseType: "blob",
      })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "ReorderSparesReport.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Excel file downloaded successfully");
      })
      .catch((err) => {
        console.error("Excel download failed", err);
        toast.error("Failed to download Excel");
      });
  };

  const handlePrint = () => {
    const payload = {
      branchId: branchId || "%",
      storeId: store || "%",
      categoryId: category || "%",
      itemName: itemName || "%",
      itemCode: itemCode || "%",
      maxstock: stockFilter || "%",
    };

    const query = new URLSearchParams(payload).toString();
    window.open(`/inventory/ReorderSparesReport?${query}`, "_blank");
  };

  const clearFilters = () => {
    setBranchId(storedBranchId || "8");
    setItemCode("");
    setItemName("");
    setStockFilter("");
    setStore("");
    setCategory("");
    setData([]);
  };

  const criticalCount = data.filter(
    (item) =>
      getStockStatus(
        parseFloat(item.fld_minstock || 0),
        parseFloat(item.fld_maxstock || 0),
        parseFloat(item.fld_closing_stock || 0)
      ) === "critical"
  ).length;

  const lowCount = data.filter(
    (item) =>
      getStockStatus(
        parseFloat(item.fld_minstock || 0),
        parseFloat(item.fld_maxstock || 0),
        parseFloat(item.fld_closing_stock || 0)
      ) === "low"
  ).length;

  const adequateCount = data.length - criticalCount - lowCount;

  return (
    <LayoutWrapper>
      <HeaderBar title="REORDER SPARES REPORT" />
      <ToastContainer />

      <div
        className={`p-6 mt-6 rounded-xl shadow-sm border ${
          isDark
            ? "bg-gray-800 border-gray-700 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold">Reorder Spares Report</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Monitor and manage spare parts inventory levels
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFilters ? primaryButtonClass : secondaryButtonClass
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${secondaryButtonClass}`}
              onClick={handleExcelDownload}
              disabled={data.length === 0}
            >
              <FaFileExcel className="w-4 h-4" />
              Export Excel
            </button>
          </div>
        </div>

        {data.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div
              className={`p-4 rounded-lg flex items-center gap-4 ${
                isDark
                  ? "bg-red-900/30 border border-red-800"
                  : "bg-red-50 border border-red-100"
              }`}
            >
              <div
                className={`p-3 rounded-full ${
                  isDark ? "bg-red-800/50" : "bg-red-100"
                }`}
              >
                <FaExclamationTriangle className="text-red-500 text-xl" />
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {criticalCount}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Critical Stock
                </div>
              </div>
            </div>

            <div
              className={`p-4 rounded-lg flex items-center gap-4 ${
                isDark
                  ? "bg-yellow-900/30 border border-yellow-800"
                  : "bg-yellow-50 border border-yellow-100"
              }`}
            >
              <div
                className={`p-3 rounded-full ${
                  isDark ? "bg-yellow-800/50" : "bg-yellow-100"
                }`}
              >
                <FaWarehouse className="text-yellow-500 text-xl" />
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {lowCount}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Low Stock
                </div>
              </div>
            </div>

            <div
              className={`p-4 rounded-lg flex items-center gap-4 ${
                isDark
                  ? "bg-green-900/30 border border-green-800"
                  : "bg-green-50 border border-green-100"
              }`}
            >
              <div
                className={`p-3 rounded-full ${
                  isDark ? "bg-green-800/50" : "bg-green-100"
                }`}
              >
                <FaBoxes className="text-green-500 text-xl" />
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {adequateCount}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Adequate Stock
                </div>
              </div>
            </div>
          </div>
        )}

        {showFilters && (
          <div
            className={`rounded-lg p-5 mb-6 ${
              isDark ? "bg-gray-700" : "bg-gray-50"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Search Filters</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 flex items-center gap-1"
              >
                <FaTimes className="w-3 h-3" /> Clear all
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-5">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Branch Location
                </label>
                <select
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-600 border-gray-300 dark:border-gray-500"
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                >
                  {branchList.map((b) => (
                    <option
                      key={b.value || b.fld_Branchid}
                      value={b.value || b.fld_Branchid}
                    >
                      {b.text || b.fld_BranchName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Store</label>
                <select
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-600 border-gray-300 dark:border-gray-500"
                  value={store}
                  onChange={(e) => setStore(e.target.value)}
                >
                  <option value="">All Stores</option>
                  {storeList.map((b) => (
                    <option
                      key={b.value || b.fld_storeid}
                      value={b.value || b.fld_storeid}
                    >
                      {b.text || b.fld_storename}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Item Category
                </label>
                <select
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-600 border-gray-300 dark:border-gray-500"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categoryList.map((b) => (
                    <option
                      key={b.value || b.fld_itemcategoryid}
                      value={b.value || b.fld_itemcategoryid}
                    >
                      {b.text || b.fld_itemcategory_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Item Code
                </label>
                <input
                  type="text"
                  value={itemCode}
                  onChange={(e) => setItemCode(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-600 border-gray-300 dark:border-gray-500"
                  placeholder="Search by item code..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Item Name
                </label>
                <input
                  type="text"
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-600 border-gray-300 dark:border-gray-500"
                  placeholder="Search by item name..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Stock Level
                </label>
                <select
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-600 border-gray-300 dark:border-gray-500"
                  value={stockFilter}
                  onChange={(e) => setStockFilter(e.target.value)}
                >
                  <option value="">All Stock Levels</option>
                  <option value="critical">Critical Stock</option>
                  <option value="low">Low Stock</option>
                  <option value="adequate">Adequate Stock</option>
                </select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-600">
              <button
                onClick={handleSearch}
                disabled={loading}
                className={`flex items-center gap-2 ${primaryButtonClass} px-4 py-2 rounded transition-colors disabled:opacity-50`}
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <FaSearch className="w-4 h-4" />
                )}
                Search
              </button>

              <button
                onClick={handleExcelDownload}
                disabled={data.length === 0}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors disabled:opacity-50"
              >
                <FaFileExcel className="w-4 h-4" />
                Export Excel
              </button>

              <button
                onClick={handlePrint}
                disabled={data.length === 0}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors disabled:opacity-50"
              >
                <FaPrint className="w-4 h-4" />
                Print Report
              </button>
            </div>
          </div>
        )}

        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">
              Results {data.length > 0 && `(${data.length} items)`}
            </h2>

            {data.length > 0 && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Last updated: {new Date().toLocaleTimeString()}
              </span>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : data.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className={`bg-${baseColor}-${baseShade}`}>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      S.No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Item Code
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Item Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Part No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      UOM
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">
                      Min Stock
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">
                      Max Stock
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">
                      Current Stock
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {data.map((item, index) => {
                    const stockStatus = getStockStatus(
                      parseFloat(item.fld_minstock || 0),
                      parseFloat(item.fld_maxstock || 0),
                      parseFloat(item.fld_closing_stock || 0)
                    );

                    return (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {index + 1}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-bold">
                          <span
                            className={
                              stockStatus === "critical"
                                ? "text-red-500"
                                : stockStatus === "low"
                                ? "text-yellow-500"
                                : "text-green-500"
                            }
                          >
                            {item.fld_itemcode}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                          {item.fld_itemname}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {item.fld_itempartno}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 rounded-lg text-xs font-semibold ${
                              isDark
                                ? "bg-blue-500/20 text-blue-300"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {item.fld_uomname}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                          {item.fld_location}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-right">
                          {item.fld_minstock}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-right">
                          {item.fld_maxstock}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-right">
                          <span
                            className={
                              stockStatus === "critical"
                                ? "text-red-500"
                                : stockStatus === "low"
                                ? "text-yellow-500"
                                : "text-green-500"
                            }
                          >
                            {item.fld_closing_stock}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                              stockStatus === "critical"
                                ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                                : stockStatus === "low"
                                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
                                : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                            }`}
                          >
                            {stockStatus === "critical" && (
                              <FaExclamationTriangle className="mr-1" />
                            )}
                            {stockStatus.charAt(0).toUpperCase() +
                              stockStatus.slice(1)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-700 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
              <FaBox className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                No inventory records
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>

        {data.length > 0 && (
          <div
            className={`mt-6 p-5 rounded-lg ${
              isDark ? "bg-gray-700" : "bg-gray-50"
            }`}
          >
            <h3 className="text-lg font-semibold mb-4">Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div
                className={`p-4 rounded-lg ${
                  isDark ? "bg-gray-600" : "bg-white shadow-sm"
                }`}
              >
                <div className="text-2xl font-bold text-center">
                  {data.length}
                </div>
                <div className="text-sm text-center text-gray-500 dark:text-gray-400 mt-1">
                  Total Items
                </div>
              </div>
              <div
                className={`p-4 rounded-lg ${
                  isDark ? "bg-gray-600" : "bg-white shadow-sm"
                }`}
              >
                <div className="text-2xl font-bold text-center text-red-600 dark:text-red-400">
                  {criticalCount}
                </div>
                <div className="text-sm text-center text-gray-500 dark:text-gray-400 mt-1">
                  Critical Stock
                </div>
              </div>
              <div
                className={`p-4 rounded-lg ${
                  isDark ? "bg-gray-600" : "bg-white shadow-sm"
                }`}
              >
                <div className="text-2xl font-bold text-center text-yellow-600 dark:text-yellow-400">
                  {lowCount}
                </div>
                <div className="text-sm text-center text-gray-500 dark:text-gray-400 mt-1">
                  Low Stock
                </div>
              </div>
              <div
                className={`p-4 rounded-lg ${
                  isDark ? "bg-gray-600" : "bg-white shadow-sm"
                }`}
              >
                <div className="text-2xl font-bold text-center text-green-600 dark:text-green-400">
                  {adequateCount}
                </div>
                <div className="text-sm text-center text-gray-500 dark:text-gray-400 mt-1">
                  Adequate Stock
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </LayoutWrapper>
  );
};

export default ReorderSparesReport;